<template>
  <ExcursionFilterPopupWrapper
    is-fullscreen
    show-close-button
    :title="t('title.header')"
    filter-key="modal"
    :is-disabled-reset-button="isDisabledResetButton"
    is-modal-only
    :modal-size="modalSize"
    modal-class="modal-filters"
    @close="closeModal"
    @apply="handleApply"
  >
    <template #trigger="{ open }">
      <div class="filters-open-btn">
        <SkeletonBlock
          :is-active="isSkeletonActive"
          :width="135"
          :height="48"
        >
          <UiButton
            v-if="!isSkeletonActive"
            :text="t('title.header')"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            :size="currentSize"
            @clicked="open"
          >
            <template #icon><ModalFilterBtnIcon /></template>
          </UiButton>
          <div
            v-if="!isSkeletonActive && filtersCount"
            class="filters-count"
          >
            {{ filtersCount }}
          </div>
        </SkeletonBlock>
      </div>
    </template>

    <div class="filters">
      <ExcursionFilterRow :title="t('title.format')">
        <ExcursionFiltersFormatContent />
      </ExcursionFilterRow>

      <ExcursionFilterRow
        :title="t('title.price', { currencySymbol })"
        class="price-row"
      >
        <ExcursionFilterPriceContent
          v-bind="props.price"
          :is-modal="true"
        />
      </ExcursionFilterRow>

      <ExcursionFilterRow :title="t('title.movementTypes')">
        <ExcursionMovementTypesList
          class="movement-types"
          :aggregation-movement-type="props.aggregationMovementType"
          :movement-types="movementTypes"
          single-column
          :is-close-modal-trigger="isCloseModalTrigger"
          @update:selectedTypes="updateMovementTypes"
        />
      </ExcursionFilterRow>

      <ExcursionFilterRow :title="t('title.startTime')">
        <StartTimeCheckboxList
          :filter-items="filtersStore.state.startTimeInterval"
          @apply="applyStartTimeIntervalFilter"
        />
      </ExcursionFilterRow>

      <ExcursionFilterRow :title="t('title.duration')">
        <DurationInterval
          v-model="filtersStore.state.durationInterval"
          :duration-interval="filtersStore.state.durationInterval"
        />
      </ExcursionFilterRow>

      <ExcursionFilterRow :title="t('title.children')">
        <ChildAllowedCheckbox
          v-model="filtersStore.state.childrenAllowed"
          :checked="!!filtersStore.state.childrenAllowed"
        />
      </ExcursionFilterRow>

      <ExcursionFilterRow :title="t('title.sort')">
        <excursion-the-sort v-model="filtersStore.state.sorting" />
      </ExcursionFilterRow>
    </div>
  </ExcursionFilterPopupWrapper>
</template>

<script setup lang="ts">
  import { UiModalSize } from '@experience/core-modal-ui';
  import { ExcursionFilterPopupWrapper } from '@/entities/filter/popup-wrapper';
  import { UiButton, UiButtonColorMode, UiButtonSize, UiButtonVariant } from '@experience/core-button-ui';
  import { ModalFilterBtnIcon } from '@/shared/ui/icons';
  import { ExcursionFiltersFormatContent } from '@/features/filter/format';
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import {
    ExcursionMovementTypesList,
    type TMovementType,
    type TMovementTypeGroup,
    useMovementTypes,
  } from '@/entities/movement-type';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';
  import type { IExperienceAggregations } from '@/entities/experience/aggregation';
  import { ExcursionFilterPriceContent } from '@/features/filter/price';
  import ExcursionFilterRow from './ExcursionFilterRow.vue';
  import { ChildAllowedCheckbox } from '@/entities/filter/child-allowed';
  import { StartTimeCheckboxList } from '@/entities/filter/start-time';
  import ExcursionTheSort from './ExcursionTheSort.vue';
  import type {
    TExcursionFiltersState,
    TExcursionStartTimeInterval,
  } from '@/entities/filter/store/types/excursion/state.ts';
  import { Sort } from '@/widgets/filtersBlock/types';
  import { DurationInterval } from '@/features/filter/duration-interval';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { useScreenSize } from '@experience/shared';
  import { type CurrencyCode, getCurrencySymbol } from '@/shared/lib/currency';
  import { useExcursionListingSkeleton } from '@/shared/composables/excursion/useExcursionDelayedListingSkeleton.ts';

  interface Props {
    isScroll: boolean;
    aggregationMovementType: IExperienceAggregations['movementType'];
    price: {
      histogram: Record<string, number>;
      minPrice: number;
      maxPrice: number;
    };
    currencyCode: CurrencyCode;
  }

  interface Emits {
    (e: 'apply', isForced: boolean, filterData?: Partial<TExcursionFiltersState>): void;
  }

  const emit = defineEmits<Emits>();

  const { isSkeletonActive } = useExcursionListingSkeleton();

  const props = defineProps<Props>();

  const currencySymbol = computed(() => getCurrencySymbol(props.currencyCode));

  const { isMobile } = useScreenSize();

  const modalSize = computed(() => (isMobile.value ? UiModalSize.FULLSCREEN : UiModalSize.S));

  const movementTypes = useMovementTypes();
  const filtersStore = useExcursionFiltersStore();

  const countCortegeFilter = (value: Array<string | number | null>) => {
    return value?.some((item) => item !== null) ? 1 : 0;
  };

  const countArrayFilter = (value: Array<string>) => {
    return value?.length ? 1 : 0;
  };

  const countBooleanFilter = (value: boolean | null) => {
    return value ? 1 : 0;
  };

  function countAppliedFilters(state: TExcursionFiltersState) {
    let count = 0;

    Object.entries(state).forEach(([key, value]) => {
      switch (key) {
        case 'price':
        case 'duration':
          count += countCortegeFilter(value as Array<string | null | number>);
          break;
        case 'startTimeInterval':
        case 'durationInterval':
        case 'movementTypes':
        case 'type':
          count += countArrayFilter(value as Array<string>);
          break;
        case 'childrenAllowed':
          count += countBooleanFilter(value as boolean);
          break;
        default:
          count += 0;
      }
    });

    return count;
  }

  const filtersCount = computed(() => {
    return countAppliedFilters(filtersStore.searchState);
  });

  const filtersSelectedCount = computed(() => {
    return countAppliedFilters(filtersStore.state);
  });

  const isDisabledResetButton = computed(() => {
    return (
      filtersSelectedCount.value < 1 &&
      filtersCount.value < 1 &&
      (!filtersStore.state.sorting || filtersStore.state.sorting === Sort.popularity)
    );
  });

  const updateMovementTypes = (selectedTypes: Array<TMovementType | TMovementTypeGroup>) => {
    filtersStore.state.movementTypes = selectedTypes;
  };

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const currentSize = computed(() => {
    if (isMobile.value) {
      return UiButtonSize.M;
    }

    return props.isScroll ? UiButtonSize.S : UiButtonSize.M;
  });

  const applyStartTimeIntervalFilter = (value: TExcursionStartTimeInterval) => {
    filtersStore.state.startTimeInterval = value;
  };

  const isCloseModalTrigger = ref(true);

  const closeModal = () => {
    isCloseModalTrigger.value = !isCloseModalTrigger.value;
  };

  const handleApply = (isForced: boolean) => {
    const filterData: Partial<TExcursionFiltersState> = {
      price: filtersStore.searchState.price,
      type: filtersStore.searchState.type,
      movementTypes: filtersStore.searchState.movementTypes,
      sorting: filtersStore.searchState.sorting,
      duration: filtersStore.searchState.duration,
      childrenAllowed: filtersStore.searchState.childrenAllowed,
      durationInterval: filtersStore.searchState.durationInterval,
      startTimeInterval: filtersStore.searchState.startTimeInterval,
    };

    emit('apply', isForced, filterData);
    closeModal();
  };
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .filters {
    display: flex;
    flex-direction: column;
    gap: 40px;

    .price-row {
      gap: 16px;
    }
  }

  .movement-types {
    min-width: 516px;
  }

  .filters-open-btn {
    position: relative;
    display: flex;
    align-items: center;

    :deep(.button_size-m) {
      @include adaptivity.mobile {
        padding: 10px 16px 10px 12px;
      }
    }
  }

  .filters-count {
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border: 2px solid var(--comp-badge-color-border-default);
    border-radius: var(--alias-radius-rounded);
    background: var(--comp-badge-color-background-neutral);
    color: var(--comp-badge-color-text);
    font-weight: var(--global-typography-weight-regular);
    font-style: normal;
    font-size: 12px;
    line-height: 16px;
    transition: 0.3s all cubic-bezier(0.77, 0, 0.18, 1);
  }
</style>

<style lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .modal-filters {
    .ui-modal__body-content-wrapper {
      padding: 0 var(--alias-space-16);
    }

    @include adaptivity.mf-tablet {
      .ui-modal__body-content-wrapper {
        padding: 0 var(--alias-space-24);
      }
    }
  }
</style>
